import { type FC } from 'react'
import { Button, GenericError, PieChart, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { type EnrollmentUserInvestmentRiskToleranceOutput } from '@egw/api/src/models/enrollment/enrollment'
import { type GQLError } from '@egw/clients'
import { type Control, Controller } from 'react-hook-form'
import { HowItWorks } from '../how-it-works/how-it-works'
import { type RiskTolerance } from '../modal-risk-tolerance/modal-risk-tolerance'

interface CashEquivalentProps {
  riskTolerance?: EnrollmentUserInvestmentRiskToleranceOutput | RiskTolerance
  isLoading?: boolean
  isSubmitting?: boolean
  error?: GQLError | null
  submitError?: GQLError | null
  onSubmit: () => void
  withContainer?: boolean
  control: Control<{
    cashEquivalentCategory: string
  }>
  isValid?: boolean
  items: { value: string; label: string; recommended?: boolean | undefined }[]
  allowTripleTaxFreeMunicipalMutualFund?: boolean
}

export const CashEquivalent: FC<CashEquivalentProps> = ({
  riskTolerance,
  isLoading = false,
  isSubmitting = false,
  error = undefined,
  submitError = undefined,
  onSubmit,
  withContainer = true,
  control,
  isValid,
  items,
  allowTripleTaxFreeMunicipalMutualFund = false,
}) => {
  const { t } = useTranslation('features-enrollment')

  return (
    <div className="flex flex-col w-full">
      <div className={`${withContainer ? 'egw-container-enroll' : ''} h-full flex flex-col`}>
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <div className="flex justify-between w-full">
              <div className="flex flex-col justify-center">
                <p className="typo-primary-2xl-light text-text-header">{t('investments.cash-equivalent.title')}</p>
                <p className="text-text-meta typo-primary-sm-regular mt-xs">
                  {riskTolerance?.allocation.cashEquivalentPercent}% {t('common.of_personal_portfolio')}
                </p>
              </div>
              {riskTolerance ? (
                <PieChart
                  segmentOne={riskTolerance.allocation.equityPercent}
                  segmentTwo={riskTolerance.allocation.fixedIncomePercent}
                  segmentThree={riskTolerance.allocation.cashEquivalentPercent}
                  highlightedSegment={3}
                  showPercentage={false}
                  showAdditionalOverlays
                  className="w-[120px] h-[100px]"
                />
              ) : null}
            </div>

            <HowItWorks
              linkText={t('common.how_it_works_title')}
              className="typo-primary-sm-semibold mt-sm"
              expandedContent={
                <div className="flex flex-col gap-3 typo-primary-sm-light text-tex-body">
                  <p>{t('investments.cash-equivalent.how_it_works.paragraph_1')}</p>
                  <p>{t('investments.cash-equivalent.how_it_works.paragraph_2')}</p>
                </div>
              }
            />

            <HowItWorks
              linkText={t('investments.cash-equivalent.about_cash_title')}
              className="mt-2xs typo-primary-sm-semibold"
              expandedContent={
                <div className="flex flex-col typo-primary-sm-light text-tex-body">
                  {allowTripleTaxFreeMunicipalMutualFund ? (
                    <>
                      <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                        {t('investments.cash-equivalent.about_cash_sectors.triple_tax_title')}
                      </p>
                      <p className="mb-xs">{t('investments.cash-equivalent.about_cash_sectors.triple_tax')}</p>
                    </>
                  ) : null}

                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.cash-equivalent.about_cash_sectors.national_municipal_title')}
                  </p>
                  <p className="mb-xs">{t('investments.cash-equivalent.about_cash_sectors.national_municipal')}</p>

                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.cash-equivalent.about_cash_sectors.treasury_bill_title')}
                  </p>
                  <p className="mb-xs">{t('investments.cash-equivalent.about_cash_sectors.treasury_bill')}</p>

                  <p className="text-text-header typo-primary-sm-semibold mb-3xs">
                    {t('investments.cash-equivalent.about_cash_sectors.corporate_bill_title')}
                  </p>
                  <p className="mb-xs">{t('investments.cash-equivalent.about_cash_sectors.corporate_bill')}</p>
                </div>
              }
            />

            <form className="mt-2xl flex flex-col justify-start md:justify-center">
              <Controller
                name="cashEquivalentCategory"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    options={items}
                    name="cashEquivalentCategory"
                    className="pb-md"
                    testId="cash-equivalent-radio"
                    large
                  />
                )}
              />
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>

      {!isLoading && !error && !submitError ? (
        <div className="bg-white sticky bottom-0 left-0 right-0 z-10">
          <div className={`${withContainer ? 'egw-container-enroll' : ''} flex flex-col `}>
            <Button
              type="submit"
              className="w-full md:w-min"
              isLoading={isSubmitting}
              testId="enrollment-continue-button"
              isDisabled={!isValid}
              onClick={onSubmit}
            >
              {t('common.continue')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
